import { ROUTES } from '@/config/routes';
import { config } from '@/config/env';
import { UploadTray } from '@/features/documents/components/UploadTray';
import { useMergeRequestFeatureQuery } from '@/features/mergeRequests/hooks/useMergeRequests';
import { ProjectSidebarTree } from '@/features/projects/components/ProjectSidebarTree';
import { WorkspaceProjectTree } from '@/features/workspace/components/WorkspaceProjectTree';
import { useCapabilities } from '@/features/workspace/hooks/useCapabilities';
import { AppLayout } from '@/shared/components/layout/AppLayout';
import type { SidebarNavItem } from '@/shared/components/layout/Sidebar';

/**
 * One navigation whatever Core supports, so `npm run dev` against a Core and `dev:local` look
 * the same. Revit models live under Projects: with the Core workspace on, Projects are workspace
 * projects and a model opens inside one; without it, Projects lists the models directly. Either
 * way there is no flat Models or Snapshots entry — snapshots are a tab of the model. The old
 * `/snapshots` routes still resolve, so existing links keep working.
 */
function navItemsFor(workspace: boolean): readonly SidebarNavItem[] {
  return [
    { label: 'Dashboard', path: ROUTES.dashboard, icon: 'dashboard', section: 'Workspace' },
    {
      label: 'Projects',
      path: workspace ? ROUTES.workspace : ROUTES.projects,
      icon: 'projects',
      section: 'Workspace',
      expandable: workspace,
    },
    { label: 'Audits', path: ROUTES.audits, icon: 'audits', section: 'Quality' },
    { label: 'Families library', path: ROUTES.families, icon: 'families', section: 'Library' },
  ];
}

/** Added next to Audits, and only while Core has the Merge Model Request feature switched on. */
const MERGE_REQUESTS_NAV_ITEM: SidebarNavItem = {
  label: 'Merge requests',
  path: ROUTES.mergeRequests,
  icon: 'cube',
};

/** Places the merge requests item with the quality tools, or last when there are none. */
function withMergeRequests(items: readonly SidebarNavItem[]): readonly SidebarNavItem[] {
  const auditsIndex = items.findIndex((item) => item.path === ROUTES.audits);
  if (auditsIndex === -1) return [...items, MERGE_REQUESTS_NAV_ITEM];

  const audits = items[auditsIndex];
  return [
    ...items.slice(0, auditsIndex + 1),
    { ...MERGE_REQUESTS_NAV_ITEM, ...(audits?.section === undefined ? {} : { section: audits.section }) },
    ...items.slice(auditsIndex + 1),
  ];
}

/**
 * The signed-in layout. Lives under `ProtectedRoute`, so the capability check never runs signed out.
 * The upload tray sits outside the routes, so uploads stay visible while the person navigates.
 */
export function AuthenticatedShell(): React.JSX.Element {
  const { capabilities } = useCapabilities();
  const mergeRequests = useMergeRequestFeatureQuery();
  const baseItems = navItemsFor(capabilities.projectWorkspace);
  const navItems = mergeRequests.data === true ? withMergeRequests(baseItems) : baseItems;

  return (
    <>
      <AppLayout
        renderProjects={(props) =>
          capabilities.projectWorkspace ? (
            <WorkspaceProjectTree {...props} />
          ) : (
            <ProjectSidebarTree {...props} />
          )
        }
        navItems={
          config.tasksBaseUrl !== null && capabilities.projectWorkspace
            ? [...navItems, { label: 'My Requests', path: ROUTES.myTasks, icon: 'inbox', section: 'Workspace' }]
            : navItems
        }
      />
      {capabilities.projectDocuments ? <UploadTray /> : null}
    </>
  );
}
